import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { DefaultSheet, SheetBody, Tabs, TabsContent, TabsList, TabsTrigger, Row } from 'twyne-new.ui';

import { HoverWrapper } from '../fields/utils';
import { visibleRules } from '../fieldsUtil';

import ControlCardPopulate from './ControlCardPopulate';
import ControlCardValidations from './ControlCardValidations';
import ControlCardVisibility from './ControlCardVisibility';
import Datasource from './Datasource';
import Properties from './Properties';

class FieldProperties extends HoverWrapper {

  state = {
    field: this.props.data || {},
  }

  booleanOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ]

  handleChangeControl = (actionType, propagateFn) => actionRules => {
    const { field } = this.state
    field.controls = field.controls || {}
    field.controls[actionType] = actionRules
    this.setState({})
    this.props.onChange(field)
    propagateFn && propagateFn()
  }

  handlePropChange = col => field => value => {
    col[field] = value
    this.setState({ field: col })
    this.props.onChange()
  }

  handleChangeProperties = col => {

    const { populate, visibility, validations } = col.controls || {}

    if (populate) {
      const { placeholder } = populate
      if (placeholder != undefined)
        col.placeholder = placeholder
    }

    if (visibility) {
      const { autohide } = visibility
      if (typeof autohide === 'boolean')
        col.autohide = autohide
    }

    if (validations) {
      const { required } = validations
      if (typeof required === 'boolean')
        col.field_required = required
    }

    if (col.disabled_dynamic !== undefined) {
      col.controls = col.controls || {}
      col.controls.disabled_dynamic = col.disabled_dynamic
    }

    this.setState({ field: col })
    this.props.onChange()
  }

  ControlCard = ({ type, visibility, Component, onChange }) => {
    const { field } = this.state
    const { [type]: value } = field.controls || { [type]: null }

    if (value === undefined || !visibility)
      return null

    return <Component onConflict={this.props.onConflict} value={value} visibility={visibility} onChange={this.handleChangeControl(type, onChange)} field={this.props.data.field} />
  }

  ControlCardPopulate = props => {
    return <ControlCardPopulate {...props} cfOptions={this.props.data.custom_field && (this.props.data.field_type === 'select' ? this.props.data.field_options : this.props.data.field_type === 'boolean' && this.booleanOptions) || null} />
  }

  ControlCardVisibility = props => {
    return <ControlCardVisibility {...props} autohide={this.props.data.autohide} />
  }

  ControlCardValidations = props => {
    return <ControlCardValidations {...props} required={this.props.data.field_required} />
  }

  Placeholder = ({ visibility: { placeholder } = {}, onChange }) => {
    if (!placeholder)
      return null

    return <ControlCardPopulate
      placeholder={this.props.data.placeholder || null}
      onChange={this.handleChangeControl('populate', onChange)}
    />
  }

  Tabs = () => {
    const { data, cols, target } = this.props
    const { populate, visibility, validations, datasource } = visibleRules(data) || {}

    useEffect(() => {
      const { populate = null, visibility = null, validations = null } = data.controls || {}
      data.controls = { populate, visibility, validations }
      this.setState({ field: data })
    }, [])

    const handleChange = () => this.handleChangeProperties(data)

    return <Tabs defaultValue='properties'>
      <TabsList>
        <TabsTrigger value='properties'>Field Properties</TabsTrigger>
        {populate && (populate.populateOptions || populate.prepopulateWith) ? <TabsTrigger value='populate'>Custom Populate</TabsTrigger> : null}
        {visibility ? <TabsTrigger value='visibility'>Custom Visibility</TabsTrigger> : null}
        {datasource ? <TabsTrigger value='datasource'>Datasource</TabsTrigger> : null}
      </TabsList>
      <TabsContent value='properties'>
        <Row>
          <Properties customFields={this.props.customFields} col={data} cols={cols} onChange={this.handleChangeProperties} target={target} selectedFields={this.props.selectedFields}>
            <this.Placeholder
              visibility={populate}
              onChange={handleChange}
            />
          </Properties>
          <this.ControlCard
            type='validations'
            visibility={validations}
            title='Validations'
            onChange={handleChange}
            Component={this.ControlCardValidations}
          />
        </Row>
      </TabsContent>
      <TabsContent value='populate'>
        <this.ControlCard
          type='populate'
          visibility={populate}
          title='Populate Control'
          onChange={handleChange}
          Component={this.ControlCardPopulate}
        />
      </TabsContent>
      <TabsContent value='visibility'>
        <this.ControlCard
          type='visibility'
          visibility={visibility}
          title='Visibility Control'
          onChange={handleChange}
          Component={this.ControlCardVisibility}
        />
      </TabsContent>
      <TabsContent value='datasource'>
        <Datasource
          col={data}
          onChange={this.handlePropChange(data)} />
      </TabsContent>
    </Tabs>
  }

  Tab = ({ tab: [tabId, label], disabled, children }) => {
    const { setEnabled } = Tabs.useTab([tabId, label])

    useEffect(() => {
      setEnabled(!disabled)
    }, [disabled])

    return <TabPane tabId={tabId}>
      {children}
    </TabPane>
  }

  ContentComponent = props => {
    return <div {...props}
      onClick={() => {
        if (this._hideTimeout)
          this.clearHideTimeout()
      }}
    />
  }

  Content = () => {
    const { target } = this.props

    useEffect(() => {
      this.updateTarget()
    }, [target])

    return <>
      <this.Tabs />
    </>
  }

  render() {
    return <this.Content />
  }
}

export default function FieldPropertiesSheet({ children, ...props }) {
  const [open, setOpen] = useState(false)
  return <>
    {!open && <>
      <div className='hidden'>
        <FieldProperties {...props} />
      </div>
    </>}

    <DefaultSheet
      triggerProps={{ asChild: true }}
      wrapperProps={{ open, onOpenChange: setOpen }}
      trigger={children}
      title={`Properties and Rules of field '${props?.data?.field}'`}
    >
      <SheetBody className='pt-2 border-b-0'>
        <FieldProperties {...props} />
      </SheetBody>
    </DefaultSheet>
  </>
}

FieldProperties.propTypes = {
  ...HoverWrapper.propTypes,
  targetContent: PropTypes.string,

  data: PropTypes.object,
  cols: PropTypes.array,
  onChange: PropTypes.func
};

FieldProperties.defaultProps = {}